import { createApi, type FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { isUnreadableReply } from "../retryConditions";
import { attemptsMade, createRetryingBaseQuery } from "../retryingBaseQuery";

export const maxRetries = 2;

/**
 * How many attempts a login call that failed with `error` made: the count the shared base query
 * recorded for that error (`attemptsMade`), which follows whatever retry budget and rule the
 * endpoint had. An error it did not return (a SerializedError, with no `status`, when something
 * threw outside the query) is judged from its type: one for a reply that could not be read, else
 * the first attempt and every retry.
 */
export function loginAttempts(error: unknown): number {
  const counted = attemptsMade(error);
  if (counted !== undefined) {
    return counted;
  }
  const fetchError = typeof error === "object" && error !== null && "status" in error;
  return fetchError && isUnreadableReply(error as FetchBaseQueryError) ? 1 : maxRetries + 1;
}

// The API address and the name of the setting that holds it, read in one place so the login page
// can name them in its error message and log instead of reading the environment a second time.
export const API_BASE_URL_KEY = "REACT_APP_LIFT_NET_CLOUD_API_ONLINE";
export const apiBaseUrl = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? "";

// The paths the login page reports when one of its three calls fails.
export const LOGIN_OFFLINE_URL = "/offline/login";
export const OFFLINE_INITIAL_PASSWORD_URL = "/offline/initial-password";
export const OWN_CUSTOM_OFFLINE_PASSWORD_URL = "/users/new-offline-psw";

// An unusable API address (a mistyped port, say) makes fetchBaseQuery throw. The shared base query
// returns that as a CUSTOM_ERROR, so the sign-in gives up after maxRetries and the page can say so.
const baseQuery = createRetryingBaseQuery(apiBaseUrl, maxRetries);

export const loginAPI = createApi({
  reducerPath: "loginAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: [],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    //* *********************************************************** */
    //* GET ME OFFLINE PASSWORD */
    getMeOfflinePSW: builder.query({
      query: (body: any) => ({
        url: `/users/offline-psw`,
        headers: { authorization: body.token },
      }),
    }),
    //* *********************************************************** */
    //* POST ME OFFLINE PASSWORD */
    postMeOfflinePSW: builder.mutation({
      query: (body: any) => ({
        url: `/users/offline-psw`,
        method: "POST",
        headers: { authorization: body.token },
      }),
    }),
    //* *********************************************************** */
    //* POST USER OFFLINE PASSWORD */
    postUserPsw: builder.mutation({
      query: (body: any) => ({
        url: `/users/offline-psw/${body.body.user_id}`,
        method: "POST",
        headers: { authorization: body.token },
      }),
    }),
    //* *********************************************************** */
    //* GET USER OFFLINE PASSWORD */
    getUserPsw: builder.query({
      query: (body: any) => ({
        url: `/users/offline-psw/${body.body.user_id}`,
        headers: { authorization: body.token },
      }),
    }),
    //* *********************************************************** */
    //* POST LOGIN OFFLINE */
    postLoginOffline: builder.mutation({
      query: (body: any) => ({
        url: LOGIN_OFFLINE_URL,
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
    }),
    //* *********************************************************** */
    //* POST OFFLINE INITIAL PASSWORD (first-login set-password) */
    postOfflineInitialPassword: builder.mutation({
      query: (body: any) => ({
        url: OFFLINE_INITIAL_PASSWORD_URL,
        method: "POST",
        body: body.body,
      }),
    }),
    //* *********************************************************** */
    //* POST OWN CUSTOM OFFLINE PASSWORD (forced change at first sign-in) */
    //
    //* Distinct from postOfflineInitialPassword above: that one is
    //* UNAUTHENTICATED and only serves accounts with no password at all. A user
    //* created on an edge server already HAS a password -- the one the server
    //* generated -- so they authenticate with it first and then replace it
    //* here, carrying the token the login issued.
    postOwnCustomOfflinePassword: builder.mutation({
      query: (body: any) => ({
        url: OWN_CUSTOM_OFFLINE_PASSWORD_URL,
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
    }),
    //* *********************************************************** */
  }),
});

export const {
  useLazyGetMeOfflinePSWQuery,
  usePostMeOfflinePSWMutation,
  useLazyGetUserPswQuery,
  usePostUserPswMutation,
  usePostLoginOfflineMutation,
  usePostOfflineInitialPasswordMutation,
  usePostOwnCustomOfflinePasswordMutation,
} = loginAPI;
